import React, { useState, useRef } from 'react';

/**
 * Parse a line of text: try to extract quantity and optional unit cost from the end.
 * e.g. "Product Name    10    5.00" or "Item A  10" or "Widget x 5"
 */
function parseLine(line) {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const numbers = trimmed.match(/\d+(?:\.\d+)?/g);
  let quantity = 1;
  let unitCost = '';
  let description = trimmed;
  if (numbers && numbers.length >= 1) {
    quantity = parseFloat(numbers[numbers.length - 1]) || 1;
    if (numbers.length >= 2) {
      unitCost = numbers[numbers.length - 2];
    }
    const lastNum = numbers[numbers.length - 1];
    const idx = trimmed.lastIndexOf(lastNum);
    description = trimmed.slice(0, idx).trim();
  }
  return { description, quantity, unitCost };
}

export default function ScanDocumentOCR({ onUse, onClose }) {
  const [imageSource, setImageSource] = useState(null);
  const [extractedText, setExtractedText] = useState('');
  const [parsedLines, setParsedLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target?.files?.[0];
    setError('');
    setExtractedText('');
    setParsedLines([]);
    if (!file) {
      setImageSource(null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file (e.g. JPG, PNG).');
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSource(url);
  };

  const handleCapture = () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera not supported in this browser.');
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        const video = document.createElement('video');
        video.srcObject = stream;
        video.play();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        video.onloadeddata = () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0);
          stream.getTracks().forEach((t) => t.stop());
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          setImageSource(dataUrl);
          setExtractedText('');
          setParsedLines([]);
        };
      })
      .catch(() => setError('Could not access camera.'));
  };

  const runOCR = async () => {
    if (!imageSource) {
      setError('Add an image first (upload or capture).');
      return;
    }
    setLoading(true);
    setError('');
    setProgress('Loading OCR engine...');
    try {
      const Tesseract = (await import('tesseract.js')).default;
      setProgress('Recognizing text...');
      const { data: { text } } = await Tesseract.recognize(
        imageSource,
        'eng',
        {
          logger: (m) => {
            if (m.status === 'recognizing text') setProgress(`OCR: ${Math.round(m.progress * 100)}%`);
          }
        }
      );
      setExtractedText(text || '');
      setParsedLines([]);
    } catch (err) {
      setError(err?.message || 'OCR failed.');
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const parseAsLines = () => {
    const lines = extractedText.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map((l) => parseLine(l)).filter(Boolean);
    setParsedLines(parsed);
  };

  const applyToDocument = () => {
    if (parsedLines.length === 0) {
      const single = parseLine(extractedText);
      if (single) onUse([single]);
      else onUse([]);
    } else {
      onUse(parsedLines);
    }
    onClose?.();
  };

  const clearAll = () => {
    setImageSource(null);
    setExtractedText('');
    setParsedLines([]);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="border rounded-lg p-4 bg-white space-y-3">
      <h4 className="font-semibold">Scan document (text recognition)</h4>
      <p className="text-sm text-gray-600">
        Upload a photo/scan of a delivery note or packing list. We’ll extract text and try to parse product lines and quantities.
      </p>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="text-sm"
        />
        <button
          type="button"
          onClick={handleCapture}
          className="border rounded px-3 py-1.5 text-sm"
        >
          Use camera
        </button>
        {imageSource && (
          <button type="button" onClick={clearAll} className="text-gray-600 text-sm">
            Clear
          </button>
        )}
      </div>

      {imageSource && (
        <div className="flex gap-4 flex-wrap">
          <img
            src={imageSource}
            alt="Document"
            className="max-h-48 rounded border object-contain"
          />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={runOCR}
              disabled={loading}
              className="bg-indigo-600 text-white px-3 py-2 rounded text-sm disabled:opacity-60"
            >
              {loading ? 'Extracting...' : 'Extract text'}
            </button>
            {progress && <span className="text-xs text-gray-500">{progress}</span>}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {extractedText && (
        <>
          <div>
            <label className="text-sm font-medium">Extracted text</label>
            <textarea
              readOnly
              className="border w-full p-2 text-sm rounded mt-1 h-32 font-mono"
              value={extractedText}
              onChange={(e) => setExtractedText(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={parseAsLines}
              className="border rounded px-3 py-1.5 text-sm"
            >
              Parse as lines
            </button>
            {parsedLines.length > 0 && (
              <button
                type="button"
                onClick={applyToDocument}
                className="bg-green-600 text-white px-3 py-1.5 rounded text-sm"
              >
                Use in document ({parsedLines.length} lines)
              </button>
            )}
            {extractedText && parsedLines.length === 0 && (
              <button
                type="button"
                onClick={applyToDocument}
                className="bg-green-600 text-white px-3 py-1.5 rounded text-sm"
              >
                Use full text as one line
              </button>
            )}
          </div>
        </>
      )}

      {parsedLines.length > 0 && (
        <div className="border rounded overflow-x-auto max-h-48 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left p-2">Description / Product</th>
                <th className="text-left p-2 w-20">Qty</th>
                <th className="text-left p-2 w-24">Unit cost</th>
              </tr>
            </thead>
            <tbody>
              {parsedLines.map((row, i) => (
                <tr key={i} className="border-b">
                  <td className="p-2">{row.description}</td>
                  <td className="p-2">{row.quantity}</td>
                  <td className="p-2">{row.unitCost || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {onClose && (
        <button type="button" onClick={onClose} className="border rounded px-3 py-1.5 text-sm">
          Close
        </button>
      )}
    </div>
  );
}
